import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import { Edit } from "@mui/icons-material";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";
import ngeohash from "ngeohash";
import { GeoPoint } from "firebase/firestore";
import moment from "moment";

import { useAuth } from "../../../../hooks";
import { icons, autoCapitalizeWords } from "../../../../constants";
import { useContributorsContext } from "../../../../providers/OrganizationAdmin/ContributorsProvider";
import { getCustomNarratorsByUser } from "../../hooks/CustomNarratorsHooks";
import QTAreasField from "../QTDetailsDataFieldsSection/QTAreasField";
import ImageCropModal from "../../../../common/ImageCropModal";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import { extractAllExifData } from "../../../../utils/exifUtils";
import { getLocationFromCoordinates } from "../../../../utils/locationUtils";
import { getCreatedByDisplay as getCreatedByDisplayLabel } from "../../../../utils/createdByUtils";
import * as exifr from "exifr";


import {
  getPlacesLibrary,
  fetchAutocompletePredictions,
  fetchPlaceDetailsNew,
  normalizePlaceDetails,
  createSessionToken,
} from "../../../../utils/googleMapsLoader";

// ─── Shared MUI sx for the fieldset-style outlined fields ───
const fieldsetFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 14,
    "& fieldset": {
      borderColor: "#C4C4C4",
    },
    "&:hover fieldset": {
      borderColor: "#00A7CD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00A7CD",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 14,
    color: "#666",
    "&.Mui-focused": {
      color: "#00A7CD",
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#000000",
  },
};

const CreateQTDataFieldsSection = ({
  quicktaleDetails,
  setQuicktaleDetails,
  handleImagePreviewModalOpen,
  handleAudioModalOpen,
  editEnabled,
  uploadImage,
  setUploadImage,
  uploadAudioFile,
  setUploadAudioFile,
  handleFileUpload,
  isSaving,
}) => {
  const { currentUser } = useAuth();
  const { GAUsers, handleGetAllGAUsers } = useContributorsContext();
  const [usersData, setUsersData] = useState([]);
  const [customNarrators, setCustomNarrators] = useState([]);
  const [mergedNarrators, setMergedNarrators] = useState([]);

  // Photo states
  const fileInputRef = useRef(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [originalExifData, setOriginalExifData] = useState(null);
  const [exifLocationModalOpen, setExifLocationModalOpen] = useState(false);
  const [pendingExifLocation, setPendingExifLocation] = useState(null);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);

  // Audio states
  const audioFileInputRef = useRef(null);
  // Inline recorder states
  const [recorderStatus, setRecorderStatus] = useState("idle"); // 'idle', 'recording', 'stopped'
  const [recorderTimer, setRecorderTimer] = useState(0);
  const [recorderBlobUrl, setRecorderBlobUrl] = useState(null);
  const [recorderBlob, setRecorderBlob] = useState(null);
  const [recorderAudioKey, setRecorderAudioKey] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const recorderStreamRef = useRef(null);
  const recorderAudioRef = useRef(null);

  // Location states
  const [locationInputValue, setLocationInputValue] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  // Surfaces the real reason the dropdown is empty instead of always claiming
  // "No locations" (e.g. an invalid key returns REQUEST_DENIED with no results).
  const [locationLookupError, setLocationLookupError] = useState("");
  const [googleServicesReady, setGoogleServicesReady] = useState(false);
  const locationSessionTokenRef = useRef(null);

  // ─── Inline recorder timer ───
  useEffect(() => {
    let interval;
    if (recorderStatus === "recording") {
      interval = setInterval(() => setRecorderTimer((t) => t + 1), 1000);
    } else if (recorderStatus === "idle") {
      setRecorderTimer(0);
    }
    return () => clearInterval(interval);
  }, [recorderStatus]);

  // Cleanup recorder stream on unmount
  useEffect(() => {
    return () => {
      if (recorderStreamRef.current) {
        recorderStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const formatRecorderTime = (time) => {
    const mins = Math.floor(time / 60).toString().padStart(2, "0");
    const secs = (time % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const startInlineRecording = async () => {
    try {
      // Reset previous recording
      if (recorderBlobUrl) {
        try { URL.revokeObjectURL(recorderBlobUrl); } catch (e) { /* noop */ }
        setRecorderBlobUrl(null);
        setRecorderBlob(null);
        setRecorderAudioKey((k) => k + 1);
      }
      if (recorderStreamRef.current) {
        recorderStreamRef.current.getTracks().forEach((t) => t.stop());
        recorderStreamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderStreamRef.current = stream;
      recorderChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) recorderChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(recorderChunksRef.current, { type: mimeType });
        setRecorderBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecorderBlobUrl(url);
        setRecorderAudioKey((k) => k + 1);
        setRecorderStatus("stopped");
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setRecorderStatus("recording");
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopInlineRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const saveInlineRecording = () => {
    if (recorderBlob) {
      handleRecordingComplete(recorderBlob);
      // Cleanup
      if (recorderBlobUrl) URL.revokeObjectURL(recorderBlobUrl);
      setRecorderBlobUrl(null);
      setRecorderBlob(null);
      setRecorderStatus("idle");
      if (recorderStreamRef.current) {
        recorderStreamRef.current.getTracks().forEach((t) => t.stop());
        recorderStreamRef.current = null;
      }
    }
  };

  const cancelInlineRecording = () => {
    if (recorderBlobUrl) URL.revokeObjectURL(recorderBlobUrl);
    if (recorderStreamRef.current) {
      recorderStreamRef.current.getTracks().forEach((t) => t.stop());
      recorderStreamRef.current = null;
    }
    setRecorderBlobUrl(null);
    setRecorderBlob(null);
    setRecorderStatus("idle");
  };

  // ─── Auto-switch isDraft ───
  useEffect(() => {
    if (isSaving?.current) {
      setTimeout(() => {
        isSaving.current = false;
      }, 100);
      return;
    }
    const hasAudio = !!(uploadAudioFile || quicktaleDetails.audioUrl);
    const hasImage = !!(uploadImage || quicktaleDetails.imageUrls?.[0]);
    const hasTitle =
      !!quicktaleDetails.title && quicktaleDetails.title.trim().length > 0;

    if (hasAudio && hasImage && hasTitle) {
      if (quicktaleDetails.isDraft !== false) {
        setQuicktaleDetails({ ...quicktaleDetails, isDraft: false });
      }
    } else {
      setQuicktaleDetails({ ...quicktaleDetails, isDraft: true });
    }
  }, [
    quicktaleDetails.audioUrl,
    quicktaleDetails.imageUrls,
    quicktaleDetails.title,
    uploadImage,
    uploadAudioFile,
  ]);

  // ─── Users & Narrators ───
  useEffect(() => {
    if (!GAUsers || GAUsers.length === 0) handleGetAllGAUsers();
  }, []);

  useEffect(() => {
    let filtered = GAUsers.filter((u) => u !== null);
    filtered = filtered.filter((u) => {
      const fn = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return fn !== "Guest User";
    });
    filtered.sort((a, b) => {
      const nA = `${a.firstName || ""} ${a.lastName || ""}`
        .toLowerCase()
        .trim();
      const nB = `${b.firstName || ""} ${b.lastName || ""}`
        .toLowerCase()
        .trim();
      return nA.localeCompare(nB);
    });
    setUsersData(filtered);
  }, [GAUsers]);

  useEffect(() => {
    const fetchCustomNarrators = async () => {
      const uid = currentUser?.userId || currentUser?.uid;
      if (uid) {
        const narrators = await getCustomNarratorsByUser(uid);
        setCustomNarrators(narrators);
      }
    };
    fetchCustomNarrators();
  }, [currentUser]);

  useEffect(() => {
    const transformed = customNarrators.map((n) => ({
      id: null,
      fullName: n.name,
    }));
    const merged = [...usersData, ...transformed];
    merged.sort((a, b) => {
      const nA =
        a.firstName && a.lastName
          ? `${a.firstName} ${a.lastName}`.toLowerCase().trim()
          : (a.fullName || "").toLowerCase().trim();
      const nB =
        b.firstName && b.lastName
          ? `${b.firstName} ${b.lastName}`.toLowerCase().trim()
          : (b.fullName || "").toLowerCase().trim();
      return nA.localeCompare(nB);
    });
    setMergedNarrators(merged);
  }, [usersData, customNarrators]);

  // ─── Google Maps init ───
  useEffect(() => {
    let cancelled = false;

    getPlacesLibrary()
      .then(() => {
        if (cancelled) return;
        setGoogleServicesReady(true);
        setLocationLookupError("");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Google Maps Places library failed to load:", error);
        setLocationLookupError("Location lookup unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchLocationSuggestions = useMemo(
    () =>
      debounce((input, callback) => {
        fetchAutocompletePredictions(input, {
          sessionToken: locationSessionTokenRef.current,
        })
          .then((predictions) => callback(predictions, null))
          .catch((error) => callback(null, error));
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;
    if (locationInputValue === "") {
      setLocationOptions(
        quicktaleDetails?.location ? [quicktaleDetails.location] : []
      );
      return;
    }

    if (!locationSessionTokenRef.current) {
      createSessionToken()
        .then((token) => {
          locationSessionTokenRef.current = token;
        })
        .catch(() => {
          // Session tokens are a billing optimization only; ignore failures.
        });
    }

    fetchLocationSuggestions(locationInputValue, (results, error) => {
      if (!active) return;

      if (error) {
        console.warn(
          `Places autocomplete failed for "${locationInputValue}":`,
          error?.message || error
        );
        setLocationLookupError("Location lookup unavailable");
      } else {
        setLocationLookupError("");
      }

      let opts = [];
      if (quicktaleDetails?.location) opts = [quicktaleDetails.location];
      if (results) opts = [...opts, ...results];
      setLocationOptions(opts);
    });
    return () => {
      active = false;
    };
  }, [
    quicktaleDetails?.location,
    locationInputValue,
    fetchLocationSuggestions,
    googleServicesReady,
  ]);

  const fetchPlaceDetails = async (placeId, prediction) => {
    const place = await fetchPlaceDetailsNew(placeId, prediction);
    const { locationDisplay, locationCountry, latitude, longitude } =
      normalizePlaceDetails(place);

    if (typeof latitude !== "number" || typeof longitude !== "number") {
      throw new Error(`Place ${placeId} returned no usable coordinates.`);
    }

    const geohash = ngeohash.encode(latitude, longitude);
    const geopoint = new GeoPoint(latitude, longitude);

    return {
      coordinates: geopoint,
      g: { geohash, geopoint },
      locationDisplay,
      locationCountry,
    };
  };

  // ─── Photo handlers ───
  // Shared processing used by both the file input and drag-and-drop.
  const processPhotoFile = async (file) => {
    if (!file) return;
    const fullExif = await extractAllExifData(file);
    setOriginalExifData(fullExif);
    const blobUrl = URL.createObjectURL(file);
    const imgData = { preview: blobUrl, raw: file };
    setUploadImage(imgData);
    setQuicktaleDetails((prev) => ({
      ...prev,
      originalPhotoNameOP: file?.name,
    }));
    setCropModalOpen(true);

    // Check EXIF GPS
    try {
      const exifData = await exifr.parse(file);
      if (exifData?.latitude && exifData?.longitude) {
        const locData = await getLocationFromCoordinates(
          exifData.latitude,
          exifData.longitude
        );
        setPendingExifLocation(locData);
        setExifLocationModalOpen(true);
      }
    } catch (err) {
      /* no EXIF */
    }
  };

  const handlePhotoInputChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      event.target.value = "";
      return;
    }
    await processPhotoFile(file);
  };

  const handlePhotoUploadClick = () => fileInputRef.current?.click();

  // ─── Photo drag-and-drop handlers ───
  const handlePhotoDragEnter = (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(true);
  };

  const handlePhotoDragOver = (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(true);
  };

  const handlePhotoDragLeave = (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(false);
  };

  const handlePhotoDrop = async (e) => {
    if (!editEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      console.warn("Dropped file is not an image:", file.type);
      return;
    }
    await processPhotoFile(file);
  };


  const handleEditPhotoClick = async () => {
    if (!originalExifData && (uploadImage?.raw || quicktaleDetails?.photoUrl)) {
      try {
        let file;
        if (uploadImage?.raw) file = uploadImage.raw;
        else if (quicktaleDetails?.photoUrl) {
          const resp = await fetch(quicktaleDetails.photoUrl);
          const blob = await resp.blob();
          file = new File([blob], "image.jpg", { type: blob.type });
        }
        if (file) {
          const data = await extractAllExifData(file);
          setOriginalExifData(data);
        }
      } catch (e) {
        /* ignore */
      }
    }
    setCropModalOpen(true);
  };

  const handleCropSave = async (editedImageData) => {
    try {
      setPhotoLoading(true);
      setUploadImage({
        preview: editedImageData.preview,
        raw: editedImageData.raw,
      });
      setCropModalOpen(false);
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleUseExifLocation = () => {
    if (pendingExifLocation) {
      setQuicktaleDetails((prev) => ({ ...prev, ...pendingExifLocation }));
      setPendingExifLocation(null);
    }
    setExifLocationModalOpen(false);
  };

  const handleDeclineExifLocation = () => {
    setPendingExifLocation(null);
    setExifLocationModalOpen(false);
  };

  // ─── Audio handlers ───
  const handleAudioFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileData = { preview: URL.createObjectURL(file), raw: file };
      setUploadAudioFile(fileData);
      setQuicktaleDetails((prev) => ({
        ...prev,
        originalAudioName: file.name,
      }));
      if (handleFileUpload) handleFileUpload(fileData, "original");
    }
  };

  const handleAudioUploadClick = () => audioFileInputRef.current?.click();

  const handleRecordingComplete = (audioBlob) => {
    const mimeType = audioBlob?.type || "audio/webm";
    const extFromMime = (type) => {
      if (!type) return "webm";
      if (type.includes("mp4")) return "mp4";
      if (type.includes("m4a")) return "m4a";
      if (type.includes("aac")) return "aac";
      if (type.includes("ogg")) return "ogg";
      if (type.includes("wav")) return "wav";
      if (type.includes("webm")) return "webm";
      return "webm";
    };
    const title = quicktaleDetails?.title?.trim();
    const baseName = title
      ? title
      : `Untitled ${new Date()
          .toLocaleString()
          .replace(/[/\\?%*:|"<>]/g, "-")}`;
    const ext = extFromMime(mimeType);
    const fileName = `${baseName}.${ext}`;
    const file = new File([audioBlob], fileName, { type: mimeType });
    const fileData = { preview: URL.createObjectURL(file), raw: file };
    setUploadAudioFile(fileData);
    setQuicktaleDetails((prev) => ({ ...prev, originalAudioName: file.name }));
    if (handleFileUpload) handleFileUpload(fileData, "original");
  };

  const hasPhoto = !!(uploadImage || quicktaleDetails?.imageUrls?.[0]);
  const photoSrc =
    uploadImage?.preview ||
    quicktaleDetails?.photoUrl ||
    quicktaleDetails?.imageUrls?.[0] ||
    null;

  const hasAudio = !!quicktaleDetails?.audioUrl;

  // ─── Autocomplete helpers ───
  // createdBy can be either a user id or an organization id. Use the shared
  // util so org names render correctly (users show "First Last /@user").
  const getCreatedByDisplay = () =>
    getCreatedByDisplayLabel(
      quicktaleDetails?.createdByData,
      quicktaleDetails?.createdBy || ""
    );


  const getToldByDisplay = () => {
    if (quicktaleDetails?.narratorData) {
      const d = quicktaleDetails.narratorData;
      const name = `${d.firstName || ""} ${d.lastName || ""}`.trim();
      return d.userName ? `${name}/@${d.userName}` : name;
    }
    return quicktaleDetails?.narrator || "";
  };

  return (
    <Grid container style={containerStyles}>
      {/* ═══════ LEFT COLUMN ═══════ */}
      <Grid item xs={12} md={6} style={{ paddingRight: 20 }}>
        {/* ── Photo Upload ── */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          {hasPhoto ? (
            <Box
              sx={{ position: "relative", display: "inline-block" }}
              onDragEnter={handlePhotoDragEnter}
              onDragOver={handlePhotoDragOver}
              onDragLeave={handlePhotoDragLeave}
              onDrop={handlePhotoDrop}
            >
              <img
                src={photoSrc}
                alt="QuickTale photo"
                onClick={handleImagePreviewModalOpen}
                style={{
                  width: 250,
                  height: 250,
                  objectFit: "contain",
                  borderRadius: 8,
                  cursor: "pointer",
                  border: isDraggingPhoto
                    ? "3px solid #008c9e"
                    : "2px solid #00A7CD",
                  boxShadow: isDraggingPhoto
                    ? "0 0 0 4px rgba(0,167,205,0.25)"
                    : "none",
                }}
              />
              {isDraggingPhoto && editEnabled && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "8px",
                    backgroundColor: "rgba(0,167,205,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#008c9e",
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "Avenir Book, sans-serif",
                    }}
                  >
                    Drop photo to replace
                  </Typography>
                </Box>
              )}

              {editEnabled && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <input
                    type="file"
                    style={{ display: "none" }}
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handlePhotoInputChange}
                  />
                  <img
                    src={icons.UploadIcon}
                    alt="upload"
                    onClick={handlePhotoUploadClick}
                    style={{ width: 24, height: 24, cursor: "pointer" }}
                  />
                  <IconButton
                    onClick={handleEditPhotoClick}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.04)",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              onClick={editEnabled ? handlePhotoUploadClick : undefined}
              onDragEnter={handlePhotoDragEnter}
              onDragOver={handlePhotoDragOver}
              onDragLeave={handlePhotoDragLeave}
              onDrop={handlePhotoDrop}
              sx={{
                width: 250,
                height: 250,
                borderRadius: 2,
                border: isDraggingPhoto
                  ? "2px dashed #008c9e"
                  : "2px dashed #00A7CD",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: editEnabled ? "pointer" : "default",
                backgroundColor: isDraggingPhoto ? "#E5F6FA" : "#F8F8F8",
                gap: 1.5,
                transition: "background-color 0.15s ease, border-color 0.15s ease",
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#00A7CD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={icons.UploadIcon}
                  alt="upload"
                  style={{
                    width: 26,
                    height: 26,
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: isDraggingPhoto ? "#008c9e" : "#595959",
                  fontSize: 14,
                  fontWeight: isDraggingPhoto ? 600 : 400,
                  fontFamily: "Avenir Book, sans-serif",
                }}
              >
                {isDraggingPhoto ? "Drop photo here" : "Add Photo"}
              </Typography>
            </Box>

          )}
          <input
            type="file"
            style={{ display: "none" }}
            accept="image/*"
            ref={fileInputRef}
            onChange={handlePhotoInputChange}
          />
        </Box>

        {/* ── Title ── */}
        <TextField
          label="Title"
          variant="outlined"
          fullWidth
          value={quicktaleDetails.title || ""}
          placeholder="Enter a Title for Your QuickTale"
          InputLabelProps={{ shrink: true }}
          onChange={autoCapitalizeWords((e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              title: e.target.value,
            })
          )}
          disabled={!editEnabled}
          sx={{
            ...fieldsetFieldSx,
            mb: 3,
            "& .MuiOutlinedInput-root": {
              ...fieldsetFieldSx["& .MuiOutlinedInput-root"],
              "& fieldset": {
                borderColor: "#00a7cd",
              },
              "&:hover fieldset": {
                borderColor: "#00a7cd",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#00a7cd",
              },
            },
            "& .MuiInputLabel-root": {
              ...fieldsetFieldSx["& .MuiInputLabel-root"],
              color: "#00a7cd",
            },
          }}
        />

        {/* ── Audio Section ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            mb: 3,
          }}
        >
          {hasAudio ? (
            /* ── Audio already uploaded/recorded ── */
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <PlayCircleFilledWhiteOutlinedIcon
                fontSize="large"
                onClick={handleAudioModalOpen}
                sx={{ color: "#00A7CD", cursor: "pointer" }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: "Avenir Book, sans-serif",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {quicktaleDetails?.originalAudioName || "Audio uploaded"}
              </Typography>
              {editEnabled && (
                <>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    accept="audio/*"
                    ref={audioFileInputRef}
                    onChange={handleAudioFileInputChange}
                  />
                  <img
                    src={icons.UploadIcon}
                    alt="upload"
                    onClick={handleAudioUploadClick}
                    style={{ width: 24, height: 24, cursor: "pointer" }}
                  />
                  <IconButton
                    onClick={startInlineRecording}
                    size="small"
                    sx={{
                      border: "2px solid #00A7CD",
                      width: 36,
                      height: 36,
                    }}
                    titleAccess="Record new audio"
                  >
                    <MicIcon sx={{ color: "#00A7CD", fontSize: 22 }} />
                  </IconButton>
                </>
              )}
            </Box>
          ) : (
            /* ── Inline recorder (no audio yet) ── */
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "center",
                width: "100%",
              }}
            >
              {editEnabled && (
                <>
                  {/* Inline recorder area */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {/* Mic / Stop button */}
                    <IconButton
                      onClick={
                        recorderStatus === "recording"
                          ? stopInlineRecording
                          : startInlineRecording
                      }
                      disabled={recorderStatus === "stopped"}
                      sx={{
                        backgroundColor:
                          recorderStatus === "recording"
                            ? "#9447AF"
                            : "#00A7CD",
                        color: "white",
                        width: 56,
                        height: 56,
                        "&:hover": {
                          backgroundColor:
                            recorderStatus === "recording"
                              ? "#7d3f94"
                              : "#008c9e",
                        },
                        "&:disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#a0a0a0",
                        },
                      }}
                    >
                      {recorderStatus === "recording" ? (
                        <StopIcon sx={{ fontSize: 32 }} />
                      ) : (
                        <MicIcon sx={{ fontSize: 32 }} />
                      )}
                    </IconButton>

                    {/* Timer */}
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontFamily: "Avenir Book, sans-serif",
                        mt: 0.5,
                        color:
                          recorderStatus === "recording"
                            ? "#9447AF"
                            : "#595959",
                        fontWeight:
                          recorderStatus === "recording" ? 600 : 400,
                      }}
                    >
                      {recorderStatus === "idle"
                        ? "Add Audio"
                        : formatRecorderTime(recorderTimer)}
                    </Typography>

                    {/* Preview + Save/Cancel after recording */}
                    {recorderStatus === "stopped" && recorderBlobUrl && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          mt: 1,
                        }}
                      >
                        <audio
                          key={recorderAudioKey}
                          ref={recorderAudioRef}
                          src={recorderBlobUrl}
                          controls
                          playsInline
                          style={{ maxWidth: 220, height: 36 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Typography
                            onClick={cancelInlineRecording}
                            sx={{
                              fontSize: 13,
                              color: "#00A7CD",
                              cursor: "pointer",
                              fontFamily: "Avenir Book, sans-serif",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Cancel
                          </Typography>
                          <Typography
                            onClick={saveInlineRecording}
                            sx={{
                              fontSize: 13,
                              color: "#fff",
                              backgroundColor: "#00A7CD",
                              borderRadius: 1,
                              px: 1.5,
                              py: 0.25,
                              cursor: "pointer",
                              fontFamily: "Avenir Book, sans-serif",
                              "&:hover": {
                                backgroundColor: "#008c9e",
                              },
                            }}
                          >
                            Save
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Upload button to the right */}
                  <input
                    type="file"
                    style={{ display: "none" }}
                    accept="audio/*"
                    ref={audioFileInputRef}
                    onChange={handleAudioFileInputChange}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      alignSelf: "flex-start",
                      mt: 1,
                    }}
                    onClick={handleAudioUploadClick}
                  >
                    <img
                      src={icons.UploadIcon}
                      alt="upload audio"
                      style={{ width: 28, height: 28 }}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Grid>

      {/* ═══════ RIGHT COLUMN ═══════ */}
      <Grid item xs={12} md={6} style={{ paddingLeft: 20 }}>
        {/* ── Created By ── */}
        <Autocomplete
          value={getCreatedByDisplay()}
          onChange={(event, newValue) => {
            if (typeof newValue === "string") {
              setQuicktaleDetails({
                ...quicktaleDetails,
                createdByData: null,
                createdBy: quicktaleDetails.createdBy,
              });
            } else if (newValue && newValue.inputValue) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                createdByData: null,
                createdBy: quicktaleDetails.createdBy,
              });
            } else if (newValue?.id) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                createdByData: newValue,
                createdBy: newValue.id,
              });
            }
          }}
          options={usersData}
          getOptionLabel={(opt) => {
            if (typeof opt === "string") return opt;
            if (opt.firstName && opt.lastName) {
              const name = `${opt.firstName} ${opt.lastName}`;
              return opt.userName ? `${name}/@${opt.userName}` : name;
            }
            return opt.fullName || opt.id || "";
          }}
          renderOption={(props, opt) => (
            <li {...props}>
              {opt.firstName && opt.lastName
                ? `${opt.firstName} ${opt.lastName}${
                    opt.userName ? `/@${opt.userName}` : ""
                  }`
                : opt.fullName || opt.name}
            </li>
          )}
          disabled={!editEnabled}
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          sx={{ mb: 3 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Created By"
              variant="outlined"
              sx={fieldsetFieldSx}
            />
          )}
        />

        {/* ── Told By ── */}
        <Autocomplete
          value={getToldByDisplay()}
          onChange={(event, newValue) => {
            if (typeof newValue === "string") {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: null,
                narrator: newValue,
              });
            } else if (newValue && newValue.inputValue) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: null,
                narrator: newValue.inputValue,
              });
            } else if (newValue) {
              if (newValue.id) {
                setQuicktaleDetails({
                  ...quicktaleDetails,
                  narratorData: newValue,
                  narrator: newValue.id,
                });
              } else {
                setQuicktaleDetails({
                  ...quicktaleDetails,
                  narratorData: null,
                  narrator: newValue.fullName,
                });
              }
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: null,
                narrator: newInputValue,
              });
            }
          }}
          filterOptions={(options, params) => {
            const { inputValue } = params;
            if (!inputValue) return options;
            return options.filter((opt) => {
              let text = "";
              if (opt.firstName && opt.lastName)
                text = `${opt.firstName} ${opt.lastName}`;
              else if (opt.name) text = opt.name;
              else text = opt.fullName || "";
              return text.toLowerCase().startsWith(inputValue.toLowerCase());
            });
          }}
          options={mergedNarrators}
          getOptionLabel={(opt) => {
            if (typeof opt === "string") return opt;
            if (opt.inputValue) return opt.inputValue;
            if (opt.firstName && opt.lastName) {
              const name = `${opt.firstName} ${opt.lastName}`;
              return opt.userName ? `${name}/@${opt.userName}` : name;
            }
            return opt.fullName || opt.id || "";
          }}
          renderOption={(props, opt) => (
            <li {...props}>
              {opt.firstName && opt.lastName
                ? `${opt.firstName} ${opt.lastName}${
                    opt.userName ? `/@${opt.userName}` : ""
                  }`
                : opt.name || opt.fullName}
            </li>
          )}
          disabled={!editEnabled}
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          sx={{ mb: 3 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Told By"
              variant="outlined"
              sx={fieldsetFieldSx}
            />
          )}
        />

        {/* ── Who Can Listen ── */}
        <TextField
          label="Who Can Listen"
          variant="outlined"
          fullWidth
          select
          value={quicktaleDetails.status || "public"}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              status: e.target.value,
            })
          }
          disabled={!editEnabled}
          sx={{
            ...fieldsetFieldSx,
            mb: 3,
            "& .MuiSelect-select": { textTransform: "capitalize" },
          }}
        >
          {["public", "private", "unlisted"].map((v) => (
            <MenuItem
              key={v}
              value={v}
              style={{ textTransform: "capitalize" }}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </MenuItem>
          ))}
        </TextField>

        {/* ── Description ── */}
        <TextField
          label="Description"
          variant="outlined"
          fullWidth
          multiline
          rows={5}
          value={quicktaleDetails.description || ""}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              description: e.target.value,
            })
          }
          disabled={!editEnabled}
          sx={{ ...fieldsetFieldSx, mb: 3 }}
        />

        {/* ── Assigned Area ── */}
        <QTAreasField
          areasMode="orgMulti"
          quicktaleDetails={quicktaleDetails}
          setQuicktaleDetails={setQuicktaleDetails}
          editEnabled={editEnabled}
        />

        {/* ── Tags ── */}
        <TextField
          label="Tags"
          variant="outlined"
          fullWidth
          value={quicktaleDetails.tags || ""}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              tags: e.target.value,
            })
          }
          disabled={!editEnabled}
          sx={{ ...fieldsetFieldSx, mb: 3 }}
        />

        {/* ── Geo Location ── */}
        <Autocomplete
          id="create-qt-location"
          popupIcon={null}
          getOptionLabel={(opt) =>
            typeof opt === "string" ? opt : opt.description
          }
          filterOptions={(x) => x}
          options={locationOptions}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={quicktaleDetails?.location || ""}
          noOptionsText={locationLookupError || "No locations"}
          onChange={async (event, newValue) => {
            try {
              setLocationOptions(
                newValue ? [newValue, ...locationOptions] : locationOptions
              );
              setLocationInputValue(newValue?.description || "");
              if (newValue && newValue.place_id) {
                const details = await fetchPlaceDetails(
                  newValue.place_id,
                  newValue
                );
                // The token is consumed by the details request; start a new
                // session for the next search.
                locationSessionTokenRef.current = null;
                setQuicktaleDetails({
                  ...quicktaleDetails,
                  location: newValue.description,
                  locationDisplay: details.locationDisplay,
                  locationCountry: details.locationCountry,
                  coordinates: details.coordinates,
                  g: details.g,
                });
              }
            } catch (err) {
              console.error("Error changing location:", err);
            }
          }}
          onInputChange={(event, newInputValue) => {
            setLocationInputValue(newInputValue);
          }}
          disabled={!editEnabled}
          sx={{ mb: 3 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Geo Location"
              variant="outlined"
              sx={fieldsetFieldSx}
            />
          )}
          renderOption={(props, option) => {
            const matches =
              option.structured_formatting?.main_text_matched_substrings || [];
            const parts = parse(
              option.structured_formatting?.main_text || "",
              matches.map((m) => [m.offset, m.offset + m.length])
            );
            return (
              <li {...props}>
                <Grid container alignItems="center">
                  <Grid item sx={{ display: "flex", width: 44 }}>
                    <LocationOnIcon sx={{ color: "text.secondary" }} />
                  </Grid>
                  <Grid
                    item
                    sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
                  >
                    {parts.map((part, index) => (
                      <Box
                        key={index}
                        component="span"
                        sx={{
                          fontWeight: part.highlight ? "bold" : "regular",
                        }}
                      >
                        {part.text}
                      </Box>
                    ))}
                    <Typography variant="body2" color="text.secondary">
                      {option.structured_formatting?.secondary_text}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      </Grid>

      {/* ─── Modals ─── */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={uploadImage?.preview || photoSrc}
        onSave={handleCropSave}
        originalFileName={quicktaleDetails?.originalPhotoName}
      />
      <ConfirmationModal
        open={exifLocationModalOpen}
        onClose={handleDeclineExifLocation}
        onConfirm={handleUseExifLocation}
        title="Use Image Location?"
        message="This image contains location data. Would you like to use the image location for this QuickTale?"
      />
    </Grid>
  );
};

export default CreateQTDataFieldsSection;

const containerStyles = {
  borderRadius: 12,
  boxShadow: "0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "#FFFFFF",
  padding: "36px 30px",
};
